import { Link } from "react-router-dom";
import { ArrowRight, ChatsCircle, Translate, BookOpenText, PencilLine, Quotes } from "@phosphor-icons/react";
import { GraderWidget } from "@/components/GraderWidget";
import { PricingCards } from "@/components/PricingCards";
import { useT } from "@/i18n";

const TESTIMONIALS = [
  { q: "My Valencia flat went from 'fine' to the first page of results. The rewrite kept every fact true, which mattered to me.", n: "Lucía M.", r: "2 apartments, Valencia" },
  { q: "I send check-in messages in German, Italian and English now without thinking about it. Guests notice.", n: "Marco B.", r: "Host, Lake Garda" },
  { q: "The guidebook link replaced a binder nobody read. Fewer 'where is the…' messages at 23:00.", n: "Ana P.", r: "Co-host, Porto" },
];

const PROBLEMS = [
  { icon: PencilLine, t: "Listings that undersell", d: "You know your place is lovely. Your description says '2 bedrooms, wifi'." },
  { icon: ChatsCircle, t: "The same 12 messages", d: "Booking, check-in, Wi-Fi, checkout, review. Typed again for every guest." },
  { icon: Translate, t: "Guests in 8 languages", d: "Your welcome book is in one. Your 1-star review came from a misunderstanding." },
];

export default function Home() {
  const { t } = useT();
  return (
    <div data-testid="home-page">
      <section className="grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-[1.05fr_1fr] gap-14 items-start">
          <div className="lg:pt-10">
            <p className="label rise">{t("For Airbnb & Booking hosts in Europe")}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#0F4C5C] rise d1" data-testid="home-hero-title">
              {t("Your listing, written like a pro.")} <em className="text-[#E36414] not-italic font-display">{t("In every language.")}</em>
            </h1>
            <p className="mt-6 text-base md:text-lg text-gray-700 max-w-xl rise d2">
              {t("Calm, practical tools from hosts, for hosts. Grade your listing in 20 seconds, rewrite it, and send guest messages and guidebooks in 10 languages.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 rise d3">
              <a href="#demo" className="btn-coral" data-testid="home-hero-grade-btn">{t("Grade my listing free")} <ArrowRight size={18} /></a>
              <Link to="/templates" className="btn-ghost" data-testid="home-hero-templates-btn">{t("Browse templates")}</Link>
            </div>
            <div className="mt-12 relative rise d4 hidden lg:block">
              <img src="https://images.unsplash.com/photo-1544965368-fc3b409098a0?auto=format&w=900" alt="A welcome guidebook on a table"
                className="rounded-2xl w-[85%] h-64 object-cover border border-[#E2DCD0]" />
              <div className="absolute -bottom-6 right-0 card p-4 bg-white w-60 shadow-lg">
                <p className="text-xs text-gray-500">{t("Avg. score after rewrite")}</p>
                <p className="font-display text-3xl text-[#0F4C5C]">54 → 86</p>
              </div>
            </div>
          </div>
          <div id="demo" className="rise d2 scroll-mt-24">
            <p className="font-display text-2xl text-[#0F4C5C] mb-4">{t("Try the Listing Grader")}</p>
            <GraderWidget compact />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0F4C5C] max-w-2xl">{t("Hosting is a lot of writing nobody told you about.")}</h2>
        <div className="mt-12 grid md:grid-cols-3 gap-8">
          {PROBLEMS.map(({ icon: I, t: title, d }, i) => (
            <div key={title} className={`card card-hover p-8 ${i === 1 ? "md:translate-y-8" : ""}`} data-testid={`problem-card-${i}`}>
              <I size={34} weight="duotone" className="text-[#E36414]" />
              <h3 className="mt-5 text-xl text-[#0F4C5C]">{t(title)}</h3>
              <p className="mt-2 text-gray-700">{t(d)}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#FCFAF8] border-y border-[#E2DCD0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid lg:grid-cols-[1fr_1.3fr] gap-12 items-center">
          <div>
            <p className="label">{t("Inside the app")}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0F4C5C]">{t("Three tools. One quiet workspace.")}</h2>
            <p className="mt-4 text-gray-700">{t("Save your properties once. Then rewrite listings, write guest messages and build guidebooks from the same facts. Nothing invented, everything editable.")}</p>
            <Link to="/pricing" className="btn-teal mt-8" data-testid="home-see-plans-btn">{t("See plans")} <ArrowRight size={18} /></Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="card p-6 col-span-2"><PencilLine size={26} className="text-[#0F4C5C]" weight="duotone" /><p className="font-display text-lg mt-3 text-[#0F4C5C]">{t("Listing rewriter")}</p><p className="text-sm text-gray-600">{t("Title, 3 paragraphs, 6 highlights. In your tone, in any of 10 languages.")}</p></div>
            <div className="card p-6"><ChatsCircle size={26} className="text-[#0F4C5C]" weight="duotone" /><p className="font-display text-lg mt-3 text-[#0F4C5C]">{t("Guest messages")}</p><p className="text-sm text-gray-600">{t("Confirmation to review request, under 120 words.")}</p></div>
            <div className="card p-6"><BookOpenText size={26} className="text-[#0F4C5C]" weight="duotone" /><p className="font-display text-lg mt-3 text-[#0F4C5C]">{t("Guidebook builder")}</p><p className="text-sm text-gray-600">{t("A shareable link and a printable PDF.")}</p></div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0F4C5C]">{t("From hosts who tried it first")}</h2>
        <div className="mt-12 grid lg:grid-cols-3 gap-8">
          {TESTIMONIALS.map((tm, i) => (
            <figure key={tm.n} className="card p-8" data-testid={`testimonial-${i}`}>
              <Quotes size={28} weight="fill" className="text-[#E36414]" />
              <blockquote className="mt-4 font-display text-lg text-gray-900 leading-snug">{t(tm.q)}</blockquote>
              <figcaption className="mt-6 text-sm"><span className="font-semibold text-[#0F4C5C]">{tm.n}</span> · <span className="text-gray-600">{t(tm.r)}</span></figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" id="pricing">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0F4C5C]">{t("Simple monthly plans")}</h2>
          <Link to="/pricing" className="text-[#E36414] font-semibold hover:underline">{t("Compare plans →")}</Link>
        </div>
        <PricingCards />
      </section>
    </div>
  );
}
